import { type ComposerHint, isPastedTable, MAX_PASTED_BYTES, MAX_TEXT_LENGTH } from '@oneapp/shared';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/icons';

/** The counter shows from here, so it doesn't distract in normal use. */
const COUNT_FROM = MAX_TEXT_LENGTH - 200;
/** About five lines; longer messages scroll inside the box. */
const MAX_HEIGHT = 132;

/**
 * Where the person types. Enter sends (Shift+Enter for a new line). When the bot asks for a code,
 * the box hides what's typed and sends it as a hidden answer that's never shown or stored.
 */
export function Composer({
  hint,
  disabled,
  onSend,
  onSecret,
  codeError,
}: {
  hint: ComposerHint | null;
  codeError?: string | undefined;
  disabled: boolean;
  onSend: (text: string) => void;
  onSecret: (value: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [text, setText] = useState('');
  const [pasteError, setPasteError] = useState(false);
  const [secret, setSecret] = useState('');
  const box = useRef<HTMLTextAreaElement>(null);
  const hidden = Boolean(hint?.secret);
  // A statement's password (Phase 16) rather than a one-time code.
  const password = hidden && hint?.secretKind === 'password';
  const pasted = hint?.pastedTables && isPastedTable(text);
  const max = pasted ? MAX_PASTED_BYTES : Math.min(hint?.maxLength ?? MAX_TEXT_LENGTH, MAX_TEXT_LENGTH);

  // Grow with the text, up to a few lines.
  // biome-ignore lint/correctness/useExhaustiveDependencies: measure again whenever the text changes
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    // scrollHeight leaves out the border; without it the box is a hair short and shows a scrollbar.
    const needed = el.scrollHeight + el.offsetHeight - el.clientHeight;
    el.style.height = `${Math.min(needed, MAX_HEIGHT)}px`;
    el.style.overflowY = needed > MAX_HEIGHT ? 'auto' : 'hidden';
  }, [text]);

  const value = hidden ? (password ? secret : secret.trim()) : text.trim();
  const submit = () => {
    if (disabled || !value || (!hidden && pasteError)) return;
    if (hidden) {
      onSecret(value);
      setSecret('');
    } else {
      onSend(value);
      setText('');
    }
  };
  const count = new Intl.NumberFormat(i18n.language === 'hi' ? 'hi-IN' : 'en-IN');

  return (
    <div className="composer-area">
      {codeError ? (
        <p role="alert" className="field__help field__help--error">
          {codeError}
        </p>
      ) : null}
      {pasteError && !hidden ? (
        <p role="alert" className="field__help field__help--error">
          {t('chat.pasteTooLarge')}
        </p>
      ) : null}
      {hidden ? (
        <p className="composer__secret-note" id={`${id}-secret`}>
          <Icon name="lock" size={14} strokeWidth={2} />
          {password ? t('chat.passwordNote') : t('chat.secretNote')}
        </p>
      ) : null}
      {!hidden && text.length >= COUNT_FROM ? (
        <p className="composer__count" id={`${id}-count`} aria-live="polite">
          {t('chat.count', { used: count.format(text.length), max: count.format(max) })}
        </p>
      ) : null}
      <form
        className="composer"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <button
          type="button"
          className="iconbtn composer__attach"
          aria-label={t('chat.attach')}
          aria-describedby={`${id}-attach`}
          disabled
        >
          <Icon name="paperclip" size={20} />
        </button>
        <span className="visually-hidden" id={`${id}-attach`}>
          {t('chat.attachSoon')}
        </span>
        <label className="visually-hidden" htmlFor={id}>
          {password ? t('chat.passwordLabel') : hidden ? t('chat.secretLabel') : t('chat.composerLabel')}
        </label>
        {hidden ? (
          <input
            id={id}
            className={`composer__field composer__field--secret${password ? ' composer__field--password' : ''}`}
            type="password"
            inputMode={hint?.inputMode ?? (password ? 'text' : 'numeric')}
            autoComplete={password ? 'off' : 'one-time-code'}
            maxLength={Math.min(hint?.maxLength ?? 64, 64)}
            value={secret}
            placeholder={hint?.placeholder ?? t('chat.secretPlaceholder')}
            aria-describedby={`${id}-secret`}
            disabled={disabled}
            spellCheck={false}
            autoCapitalize="off"
            onChange={(event) => setSecret(event.target.value)}
          />
        ) : (
          <textarea
            id={id}
            ref={box}
            rows={1}
            className="composer__field"
            value={text}
            maxLength={max}
            placeholder={hint?.placeholder ?? t('chat.composerPlaceholder')}
            inputMode={hint?.inputMode}
            enterKeyHint="send"
            disabled={disabled}
            aria-describedby={text.length >= COUNT_FROM ? `${id}-count` : undefined}
            onPaste={(event) => {
              if (!hint?.pastedTables) return;
              const element = event.currentTarget;
              const next =
                text.slice(0, element.selectionStart) +
                event.clipboardData.getData('text') +
                text.slice(element.selectionEnd);
              if (isPastedTable(next)) {
                event.preventDefault();
                const tooLarge = new TextEncoder().encode(next).length > MAX_PASTED_BYTES;
                setPasteError(tooLarge);
                if (tooLarge) return;
                setText(next);
              }
            }}
            onChange={(event) => {
              const next = hint?.uppercase ? event.target.value.toUpperCase() : event.target.value;
              setText(next);
              setPasteError(Boolean(hint?.pastedTables && new TextEncoder().encode(next).length > MAX_PASTED_BYTES));
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                submit();
              }
            }}
          />
        )}
        <button
          type="submit"
          className="iconbtn composer__send"
          aria-label={t('chat.send')}
          disabled={disabled || !value || (!hidden && pasteError)}
        >
          <Icon name="send" size={19} />
        </button>
      </form>
    </div>
  );
}
